/**
 * <StudentSettingsGeneralPage> — « Général », la première page des paramètres de l'élève (page).
 *
 * Les champs de l'onglet d'aujourd'hui, dans son ordre : la ligne d'abonnement telle que l'élève
 * la lit, ses coordonnées, celles d'un parent, la note sur les adresses email, et l'entrée vers
 * le mot de passe. Les verrous sont ceux d'aujourd'hui : l'élève ne change ni son adresse email ni
 * le téléphone de son parent.
 *
 * La ligne d'abonnement n'ouvre « Mes privilèges » que si `onOpenSubscription` est donné (un
 * abonnement en cours ou en pause) ; sinon elle se lit, sans chevron et sans rien promettre.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { SettingsSection, SettingsSectionSkeleton } from "../../molecules/SettingsSection"
import {
    AccountFormCardSkeleton,
    AccountSettingsTemplate,
    type AccountSettingsShellProps,
} from "../../templates/AccountSettingsTemplate"
import { type UIStudentGeneralSettings, type UIStudentSubscriptionSummary } from "../../../ports/primary/ui/types"

export type { UIStudentGeneralSettings, UIStudentSubscriptionSummary }

export type StudentGeneralSettingsField = keyof UIStudentGeneralSettings

export type StudentSettingsGeneralPageProps = AccountSettingsShellProps & {
    /** `null` tant que le compte n'est pas là. */
    values: UIStudentGeneralSettings | null
    errors?: Partial<Record<StudentGeneralSettingsField, string>>
    onChange: (field: StudentGeneralSettingsField, value: string) => void
    subscription?: UIStudentSubscriptionSummary | null
    /** Ouvre « Mes privilèges ». Présent seulement pour un abonnement en cours ou en pause. */
    onOpenSubscription?: () => void
    onContactSupport: () => void
    onChangePassword: () => void
}

function GeneralSkeleton() {
    return (
        <Stack gap="4">
            <SettingsSectionSkeleton rows={1} />
            <AccountFormCardSkeleton fields={4} />
            <AccountFormCardSkeleton fields={4} />
        </Stack>
    )
}

export function StudentSettingsGeneralPage(props: StudentSettingsGeneralPageProps) {
    const {
        values,
        errors = {},
        onChange,
        subscription,
        onOpenSubscription,
        onContactSupport,
        onChangePassword,
        ...shell
    } = props

    const busy = shell.submitting === true
    const field = (name: StudentGeneralSettingsField) => ({
        value: values?.[name] ?? "",
        onChangeText: (text: string) => onChange(name, text),
        error: errors[name],
    })

    const trial = subscription?.trialEndsOn

    return (
        <AccountSettingsTemplate {...shell} title="Général" skeleton={<GeneralSkeleton />}>
            {values != null && (
                <Stack gap="4">
                    {subscription != null && (
                        <SettingsSection
                            rows={[
                                {
                                    key: "subscription",
                                    iconName: "credit-card",
                                    label: subscription.planLabel,
                                    description:
                                        trial != null && trial !== ""
                                            ? `Fin de l'essai gratuit : le ${trial}`
                                            : undefined,
                                    value: trial == null && onOpenSubscription != null ? "Voir les détails" : undefined,
                                    trailing:
                                        subscription.holidayCourse === true ? (
                                            <Badge text="Stage" variant="neutral" />
                                        ) : undefined,
                                    onPress: onOpenSubscription,
                                },
                            ]}
                        />
                    )}

                    <Card p="4">
                        <Stack gap="3">
                            <TextInput {...field("firstname")} label="Prénom" placeholder="Prénom" disabled={busy} />
                            <TextInput {...field("lastname")} label="Nom" placeholder="Nom" disabled={busy} />
                            <TextInput
                                {...field("mobile")}
                                label="Numéro de téléphone"
                                placeholder="ex : 0601020304"
                                keyboardType="phone-pad"
                                textContentType="telephoneNumber"
                                disabled={busy}
                            />
                            <TextInput
                                {...field("email")}
                                label="Adresse email"
                                placeholder="Adresse email"
                                textContentType="emailAddress"
                                disabled
                            />
                        </Stack>
                    </Card>

                    <Card p="4">
                        <Stack gap="3">
                            <TextInput
                                {...field("parentFirstname")}
                                label="Prénom d'un parent"
                                placeholder="Prénom du parent"
                                disabled={busy}
                            />
                            <TextInput
                                {...field("parentLastname")}
                                label="Nom d'un parent"
                                placeholder="Nom du parent"
                                disabled={busy}
                            />
                            <TextInput
                                {...field("parentMobile")}
                                label="Numéro de téléphone d'un parent"
                                placeholder="ex : 0601020304"
                                keyboardType="phone-pad"
                                textContentType="telephoneNumber"
                                disabled
                            />
                            <TextInput
                                {...field("parentEmail")}
                                label="Adresse email d'un parent"
                                placeholder="Adresse email d'un parent"
                                keyboardType="email-address"
                                autoCapitalize="none"
                                disabled={busy}
                            />
                        </Stack>
                    </Card>

                    <Row gap="1" align="center" justify="center" style={styles.note}>
                        <Text size="text-md" color="text-muted" center>
                            Si tu souhaites modifier une adresse email,
                        </Text>
                        <TextButton label="contacte le support" size="sm" onPress={onContactSupport} />
                    </Row>

                    <SettingsSection
                        rows={[
                            {
                                key: "password",
                                iconName: "lock",
                                label: "Modifier mon mot de passe",
                                onPress: onChangePassword,
                            },
                        ]}
                    />
                </Stack>
            )}
        </AccountSettingsTemplate>
    )
}

const styles = StyleSheet.create(() => ({
    note: { flexWrap: "wrap" },
}))
